import {createTheme, ThemeOptions, responsiveFontSizes} from '@mui/material/styles';

export const themeConfig: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#F22643',
      light: '#F77D8E',
      dark: '#9D2538'
    },
    secondary: {
      main: '#212124',
      light: '#4D4D4F',
      dark: '#171719'
    },
    error: {
      main: '#731221',
      light: '#8F414D',
      dark: '#500C17'
    },
    warning: {
      main: '#F26233',
      light: '#F4815B',
      dark: '#A94423'
    },
    info: {
      main: '#1BAFF2',
      light: '#48BFF4',
      dark: '#127AA9'
    },
    success: {
      main: '#00BF26',
      light: '#D0FFD9',
      dark: '#025C26'
    },
    grey: {
      50: '#F4F4F4',
      100: '#E8E8E8',
      200: '#C7C7C7',
      300: '#8E8E8E',
      400: '#6B6B6B',
      900: '#212124'
    },
    divider: '#E8E8E8',
    common: {
      mds_global_color_teal: '#08C6BA',
      mds_global_color_yellow: '#FFCF00',
      mds_global_color_yellow_2: '#5C4B00',
      mds_global_color_red_5: '#FFDADF',
      mds_global_color_red_6: '#731221',
      mds_global_color_red_7: '#D21E3A',
      mds_global_color_red_8: '#E84861',
      mds_global_color_red_9: '#F8505A',
      mds_global_color_red_10: '#F226A0',
      mds_global_color_blue_20: '#ECF8FE',
      mds_global_color_orange_20: '#FEF0EC',
      mds_global_color_orange_80: '#FFFAED',
      mds_global_color_orange_90: '#FFAF37',
      mds_global_color_green_70: '#F7FCF8',
      mds_global_color_red_15: '#FEE9EC',
      mds_global_color_red_25: '#E82026',
      mds_global_color_green_20: '#D0FFD9',
      mds_global_color_green_30: '#026429',
      mds_global_color_green_40: '#0B8A1C',
      mds_global_color_transparent: 'transparent',
      mds_global_color_white: '#FFFFFF',
      mds_global_color_orange: '#FF6432',
      mds_global_background_red: '#FEE9EC',
      mds_global_background_yellow: '#FFF9E0',
      mds_global_background_green: '#EBFFEF',
      mds_global_color_purple: '#6109FF',
      mds_global_color_purple_light: '#EEE6FF',
      mds_global_color_purple_light_50: '#EADEFF',
      mds_global_background_orange: '#FFECE6',
      mds_global_background_gray: '#3e323375',
      mds_global_background_red_1: '#FF0000',
      mds_global_color_purple_1: '#8057F6',
      mds_global_color_orange_10: '#FF8F00',
      mds_global_color_light_blue: '#EBF9FF',
      mds_global_color_dark_blue: '#004BAO',
      mds_global_color_green_50: '#E2FFED',
      mds_global_color_green_60: '#B3FFCF',
      mds_global_color_green_80: '#006013',
      mds_global_color_green_90: '#98FFBD',
      mds_global_color_blue_10: '#2F8AF4',
      mds_global_color_red: '#F22643',
      mds_global_color_blue_30: '#1274E6'
    }
  },
  typography: {
    fontFamily: ['Noto Sans Thai', 'sans-serif'].join(','),
    fontSize: 16,
    fontWeightRegular: 400,
    fontWeightLight: 300,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600
    },
    h5: {
      fontSize: '1.2rem',
      fontWeight: 500
    },
    h6: {
      fontSize: '1rem',
      lineHeight: 1.25,
      fontWeight: 500
    },
    subtitle1: {
      lineHeight: 1.5
    },
    subtitle2: {
      fontWeight: 400,
      lineHeight: 1.25,
      // TODO: update this font with looped thai, currently google has no different using the sans thai and looped thai.
      fontFamily: 'Noto Sans Thai'
    },
    body1: {
      lineHeight: 1.35,
      fontSize: '0.875rem'
    },
    body2: {
      lineHeight: 1.35,
      fontSize: `0.875rem`,
      fontWeight: 400
    },
    button: {
      fontSize: '1rem',
      fontWeight: 600,
      textTransform: 'none'
    },
    caption: {
      fontSize: '0.8rem',
      lineHeight: 1.6
    }
  },
  spacing: 8,
  shape: {
    borderRadius: 4
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      mobile: 280,
      tablet: 768,
      desktop: 1204
    }
  },
  mixins: {
    toolbar: {
      height: '0.5rem',
      width: '1rem'
    }
  },
  components: {
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true
      }
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        html: {height: '100%', overflow: 'auto'},
        '::-webkit-scrollbar': {
          '-webkit-appearance': 'none',
          width: '6px',
          height: '9px'
        },
        '::-webkit-scrollbar-thumb': {
          borderRadius: '6px',
          backgroundColor: 'rgba(142, 142, 142, 1)',
          boxshadow: '0 0 1px rgba(255, 255, 255, 0.5)'
        },
        '#onetrust-consent-sdk': {
          display: 'none'
        },
        '#onetrust-banner-sdk .ot-sdk-container .ot-sdk-row': {
          [theme.breakpoints.up('tablet')]: {
            width: '100%',
            display: 'grid',
            margin: 'auto',
            gridTemplateColumns: 'auto auto',
            columnGap: theme.spacing(2),
            alignItems: 'center',
            '#onetrust-policy': {
              margin: theme.spacing(3, 0, 1.25, 3)
            },
            '#onetrust-button-group-parent': {
              position: 'static !important',
              transform: 'none',
              width: 'max-content',
              '#onetrust-button-group': {
                margin: 0,
                'button#onetrust-accept-btn-handler': {
                  marginRight: theme.spacing(3)
                }
              }
            },
            '#onetrust-group-container': {
              width: '100%'
            }
          },
          [theme.breakpoints.up('desktop')]: {
            width: theme.spacing(99),
            '#onetrust-policy': {
              margin: theme.spacing(3, 0, 1.25)
            }
          },
          'button#onetrust-pc-btn-handler, button#onetrust-accept-btn-handler': {
            borderRadius: theme.spacing(4.5),
            [theme.breakpoints.up('tablet')]: {
              marginBottom: 0,
              marginTop: 0
            }
          }
        }
      })
    }
  }
};

const theme = createTheme(themeConfig);

export const DefaultThemeOptions = responsiveFontSizes(theme);

export default DefaultThemeOptions;
